import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

import { trapTabKey } from "../../tasksShared/lib/focusTrap";
import {
  COOPERATION_CURRENCY_OPTIONS,
  computeFrozenRewardPreview,
  formatCooperationBadgeText,
  hasVisibleCooperationOffer,
} from "../../realty/lib/cooperationOffer";
import { proposeDeal, startInternalDeal } from "../api/deals";
import {
  clientPickerLabel,
  searchDealEligibleClients,
  unwrapPagedResults,
} from "../api/dealEligibleClients";
import { partyDisplayName } from "../lib/dealFormatting";

const SEARCH_DEBOUNCE_MS = 300;

const EDITABLE_FIELDS = [
  "property",
  "client_id",
  "price",
  "currency",
  "proposal_note",
];

const PRIVACY_404_MESSAGE =
  "Об'єкт недоступний або цю дію зараз виконати неможливо.";

const DUPLICATE_PROPOSAL_CODE = "deal_duplicate_proposal_conflict";
const DUPLICATE_PROPOSAL_MESSAGE =
  "Така пропозиція щодо угоди вже існує.";

function isAbortError(error) {
  if (!error) return false;
  return (
    error.name === "AbortError"
    || error.name === "CanceledError"
    || error.code === "ERR_CANCELED"
  );
}

/**
 * Accept only safe positive integer Deal IDs for navigation.
 * Rejects missing, zero, negative, decimal, and non-numeric values.
 */
export function parseExistingDealId(value) {
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value <= 0) return null;
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed <= 0) return null;
    return parsed;
  }
  return null;
}

function isDuplicateProposalConflict(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return false;
  return data.code === DUPLICATE_PROPOSAL_CODE;
}

function formatRewardPreview(preview) {
  if (!preview || !preview.ok) {
    if (preview && preview.reason === "currency_mismatch") {
      return `Валюта угоди (${preview.dealCurrency}) не збігається з валютою винагороди (${preview.offerCurrency}).`;
    }
    return null;
  }
  const amount = Number(preview.amount);
  const formatted = Number.isFinite(amount)
    ? amount
        .toLocaleString("uk-UA", { maximumFractionDigits: 2 })
        .replace(/\u00a0/g, " ")
    : "—";
  const currency = preview.currency || "";
  if (preview.type === "percent") {
    const pct = Number(preview.percent);
    const pctLabel = Number.isFinite(pct)
      ? `${pct.toLocaleString("uk-UA", { maximumFractionDigits: 2 })}%`
      : "";
    return `Заморожена винагорода: ${pctLabel} → ${formatted}${currency ? ` ${currency}` : ""}`;
  }
  return `Заморожена винагорода: ${formatted}${currency ? ` ${currency}` : ""}`;
}

/**
 * Controlled modal for typed A2 Deal creation.
 * mode="propose" → POST /realty/deals/propose/
 * mode="start-internal" → POST /realty/deals/start-internal/
 */
function DealCreateFlowModal({ open, mode, property, onClose, onSuccess }) {
  const titleId = useId();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const submittingRef = useRef(false);
  const searchGenRef = useRef(0);
  const searchControllerRef = useRef(null);
  const submitControllerRef = useRef(null);
  const mountedRef = useRef(true);

  const [clientSearch, setClientSearch] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoadState, setClientLoadState] = useState("idle");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientLabel, setSelectedClientLabel] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [proposalNote, setProposalNote] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [existingDealId, setExistingDealId] = useState(null);
  const [submitState, setSubmitState] = useState("idle");

  const offer = property && property.cooperation_offer;
  const isFixedOffer = Boolean(
    hasVisibleCooperationOffer(offer) && offer.type === "fixed",
  );
  const lockedCurrency =
    isFixedOffer && offer.currency ? offer.currency : null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (submitControllerRef.current) {
        submitControllerRef.current.abort();
        submitControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setClientSearch("");
    setClientOptions([]);
    setClientLoadState("idle");
    setSelectedClientId("");
    setSelectedClientLabel("");
    setPrice(
      property && property.price != null && property.price !== ""
        ? String(property.price)
        : "",
    );
    const offerOnOpen = property && property.cooperation_offer;
    const fixedOnOpen = Boolean(
      hasVisibleCooperationOffer(offerOnOpen) &&
        offerOnOpen.type === "fixed",
    );
    const nextCurrency =
      (fixedOnOpen && offerOnOpen.currency) ||
      (property && property.currency) ||
      "USD";
    setCurrency(nextCurrency);
    setProposalNote("");
    setFieldErrors({});
    setFormError(null);
    setExistingDealId(null);
    setSubmitState("idle");
    submittingRef.current = false;
    return undefined;
  }, [open, mode, property]);

  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const timer = window.setTimeout(() => {
      const input = dialogRef.current?.querySelector(
        "#deal-create-client-search",
      );
      if (input && typeof input.focus === "function") input.focus();
    }, 0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (submittingRef.current) return;
        onClose();
        return;
      }
      trapTabKey(event, dialogRef.current);
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === "function"
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      searchGenRef.current += 1;
      const generation = searchGenRef.current;
      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
      const controller = new AbortController();
      searchControllerRef.current = controller;
      setClientLoadState("loading");
      searchDealEligibleClients(
        { search: clientSearch },
        { signal: controller.signal },
      )
        .then((data) => {
          if (generation !== searchGenRef.current) return;
          setClientOptions(unwrapPagedResults(data));
          setClientLoadState("loaded");
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          if (generation !== searchGenRef.current) return;
          setClientOptions([]);
          setClientLoadState("error");
          setFormError((prev) => prev || messageFromError(err));
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      if (searchControllerRef.current) {
        searchControllerRef.current.abort();
      }
    };
  }, [open, clientSearch]);

  if (!open) return null;

  const submitting = submitState === "submitting";
  const isPropose = mode === "propose";
  const heading = isPropose
    ? "Запропонувати клієнта"
    : "Створити внутрішню угоду";
  const submitLabel = isPropose
    ? "Надіслати пропозицію"
    : "Створити угоду";

  const owner = property && property.created_by;
  const badgeText = formatCooperationBadgeText(offer, property);
  const rewardPreview = computeFrozenRewardPreview({
    offer,
    dealPrice: price,
    dealCurrency: lockedCurrency || currency,
  });
  const rewardPreviewText = formatRewardPreview(rewardPreview);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
    setExistingDealId(null);
  };

  const handleClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  const handleGoToExistingDeal = () => {
    if (existingDealId == null) return;
    navigate(`/deals/${existingDealId}`);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting || submittingRef.current) return;
    if (!property || property.id == null) {
      setFormError("Об'єкт не задано.");
      return;
    }
    if (!selectedClientId) {
      setFieldErrors({ client_id: "Оберіть клієнта." });
      return;
    }
    if (rewardPreview && !rewardPreview.ok && rewardPreview.reason === "currency_mismatch") {
      // Propose mode hides currency controls — surface as form-level guidance.
      if (isPropose) {
        setFormError(rewardPreviewText || "Валюта не збігається з винагородою.");
        setFieldErrors({});
      } else {
        setFieldErrors({
          currency: rewardPreviewText || "Валюта не збігається з винагородою.",
        });
      }
      return;
    }

    submittingRef.current = true;
    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);
    setExistingDealId(null);

    const payload = {
      property: property.id,
      client_id: Number(selectedClientId) || selectedClientId,
      price,
      currency: lockedCurrency || currency,
      proposal_note: proposalNote.trim() ? proposalNote.trim() : undefined,
    };

    if (submitControllerRef.current) {
      // Do not treat a prior controller as a retry seed; replace only.
      submitControllerRef.current = null;
    }
    const controller = new AbortController();
    submitControllerRef.current = controller;

    try {
      const api = isPropose ? proposeDeal : startInternalDeal;
      const deal = await api(payload, { signal: controller.signal });
      if (controller.signal.aborted || !mountedRef.current) {
        return;
      }
      submittingRef.current = false;
      setSubmitState("idle");
      if (typeof onSuccess === "function") {
        onSuccess(deal);
      }
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted || !mountedRef.current) {
        // Client abort does not guarantee server rollback if POST already landed.
        return;
      }
      const httpStatus = error && error.response && error.response.status;
      if (httpStatus === 404) {
        setFormError(PRIVACY_404_MESSAGE);
        setFieldErrors({});
        setExistingDealId(null);
      } else if (httpStatus === 409 && isDuplicateProposalConflict(error)) {
        // Exact duplicate-proposal contract — no auto-resubmit.
        const data = error.response.data;
        setFormError(
          typeof data.detail === "string" && data.detail
            ? data.detail
            : DUPLICATE_PROPOSAL_MESSAGE,
        );
        setExistingDealId(parseExistingDealId(data.existing_deal_id));
        setFieldErrors({});
      } else if (httpStatus === 409) {
        // Show conflict message; do not auto-resubmit.
        setFormError(messageFromError(error));
        setFieldErrors({});
        setExistingDealId(null);
      } else {
        const parsed = parseDrfFieldErrors(error, EDITABLE_FIELDS);
        if (isPropose) {
          // Price/currency are not editable in propose mode — do not attach
          // invisible field errors the user cannot correct in-place.
          const proposeFieldErrors = {};
          if (parsed.client_id) proposeFieldErrors.client_id = parsed.client_id;
          if (parsed.proposal_note) {
            proposeFieldErrors.proposal_note = parsed.proposal_note;
          }
          const hiddenFieldMsg =
            parsed.price || parsed.currency || null;
          setFieldErrors(proposeFieldErrors);
          setFormError(
            parsed._form
              || (typeof hiddenFieldMsg === "string" ? hiddenFieldMsg : null)
              || null,
          );
        } else {
          setFieldErrors(parsed);
          setFormError(parsed._form || null);
        }
        setExistingDealId(null);
      }
      submittingRef.current = false;
      setSubmitState("idle");
    } finally {
      if (submitControllerRef.current === controller) {
        submitControllerRef.current = null;
      }
    }
  };

  const effectiveCurrency = lockedCurrency || currency;

  return (
    <div
      className="feature-clients-list__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-mode={mode}
    >
      <div
        className="feature-clients-list__modal-backdrop"
        aria-hidden="true"
        onClick={handleClose}
      />
      <div className="feature-clients-list__modal-dialog" ref={dialogRef}>
        <header className="feature-clients-list__modal-head">
          <h2 id={titleId} className="feature-clients-list__modal-title">
            <i
              className={
                isPropose
                  ? "fa-solid fa-handshake"
                  : "fa-solid fa-file-signature"
              }
              aria-hidden="true"
            />
            <span>{heading}</span>
          </h2>
          <button
            type="button"
            className="feature-clients-list__modal-close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Закрити"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <form
          className="ui-form feature-clients-list__modal-body"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="ui-form__rows">
            <div role="region" aria-label="Об'єкт">
              <p>
                <strong>{(property && property.title) || "Без назви"}</strong>
              </p>
              {owner ? (
                <p className="form-hint">
                  Власник: {partyDisplayName(owner)}
                </p>
              ) : null}
              {badgeText ? (
                <p className="form-hint" role="note">
                  {badgeText}
                </p>
              ) : null}
            </div>

            <fieldset>
              <legend>Клієнт</legend>
              <label
                className="visually-hidden"
                htmlFor="deal-create-client-search"
              >
                Пошук клієнта
              </label>
              <TextInput
                id="deal-create-client-search"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  clearFieldError("client_id");
                }}
                placeholder="Пошук клієнта"
                disabled={submitting}
                aria-label="Пошук клієнта"
              />
              {clientLoadState === "loading" ? (
                <Spinner size="sm" label="Завантаження клієнтів" />
              ) : null}
              {clientLoadState === "error" ? (
                <p className="form-hint text-danger" role="alert">
                  Не вдалося завантажити клієнтів.
                </p>
              ) : null}
              {clientLoadState === "loaded" && clientOptions.length === 0 ? (
                <p className="form-hint" role="status">
                  Підходящих клієнтів не знайдено.
                </p>
              ) : null}
              <div
                role="group"
                aria-label="Кандидати клієнтів"
              >
                {clientOptions.map((client) => {
                  const label = clientPickerLabel(client);
                  const selected =
                    String(selectedClientId) === String(client.id);
                  return (
                    <button
                      key={client.id}
                      type="button"
                      aria-pressed={selected}
                      className="action-btn"
                      style={{
                        display: "block",
                        width: "100%",
                        marginTop: "0.35rem",
                        textAlign: "left",
                        opacity: selected ? 1 : 0.85,
                      }}
                      onClick={() => {
                        setSelectedClientId(String(client.id));
                        setSelectedClientLabel(label);
                        clearFieldError("client_id");
                      }}
                      disabled={submitting}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {selectedClientId ? (
                <p className="form-hint" role="status">
                  Обрано: {selectedClientLabel || `Клієнт #${selectedClientId}`}
                </p>
              ) : null}
              {fieldErrors.client_id ? (
                <InlineError>{fieldErrors.client_id}</InlineError>
              ) : null}
            </fieldset>

            {!isPropose ? (
              <>
                <Field
                  id="deal-create-price"
                  label="Ціна угоди"
                  required
                  error={fieldErrors.price}
                >
                  <TextInput
                    id="deal-create-price"
                    type="number"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => {
                      setPrice(e.target.value);
                      clearFieldError("price");
                    }}
                    disabled={submitting}
                    aria-required="true"
                  />
                </Field>

                <Field
                  id="deal-create-currency"
                  label="Валюта"
                  required
                  error={fieldErrors.currency}
                >
                  <Select
                    id="deal-create-currency"
                    value={effectiveCurrency}
                    onChange={(e) => {
                      if (lockedCurrency) return;
                      setCurrency(e.target.value);
                      clearFieldError("currency");
                    }}
                    options={COOPERATION_CURRENCY_OPTIONS}
                    disabled={submitting || Boolean(lockedCurrency)}
                    aria-required="true"
                  />
                </Field>
                {lockedCurrency ? (
                  <p className="form-hint" role="note">
                    Валюта зафіксована пропозицією винагороди ({lockedCurrency}).
                  </p>
                ) : null}
              </>
            ) : null}

            <Field
              id="deal-create-note"
              label="Примітка (необов'язково)"
              error={fieldErrors.proposal_note}
            >
              <Textarea
                id="deal-create-note"
                value={proposalNote}
                onChange={(e) => {
                  setProposalNote(e.target.value);
                  clearFieldError("proposal_note");
                }}
                rows={3}
                disabled={submitting}
                maxLength={2000}
              />
            </Field>

            {rewardPreviewText ? (
              <p
                className={
                  rewardPreview && rewardPreview.ok
                    ? "form-hint"
                    : "form-hint text-danger"
                }
                role="status"
                aria-live="polite"
              >
                {rewardPreviewText}
              </p>
            ) : null}

            {fieldErrors.property ? (
              <InlineError>{fieldErrors.property}</InlineError>
            ) : null}
            {formError ? (
              <p className="form-hint text-danger" role="alert">
                {formError}
              </p>
            ) : null}
            {existingDealId != null ? (
              <p className="form-hint" role="status">
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleGoToExistingDeal}
                  disabled={submitting}
                >
                  Перейти до угоди
                </button>
              </p>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            <button
              type="button"
              className="action-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              Скасувати
            </button>
            <button
              type="submit"
              className="action-btn"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? "Надсилання…" : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DealCreateFlowModal;
